import React, { useState, useEffect } from 'react'
import { Button, Space } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { carsUrl, getRequest, errorNotification } from '../../config'
import PageLoader from '../../components/Layouts/PageLoader'
import PageTitle from 'components/PageTitle'
import DetailSection from './DetailSection'
import TableSection from './TableSection'
import RecentChangesCard from '../InventoryAssistant/RecentChangesCard'

/**
 * The car detail, rendered either as the routed page (`mode="page"`) or inside
 * the list's quick-view modal (`mode="modal"`). Same component both ways, so the
 * two can never drift apart.
 *
 * It fetches by id rather than taking the row the table already has: the modal is
 * opened from a list that may have been sitting on screen for a while, and the
 * detail is where prices and status are acted on. Paired with `destroyOnClose` on
 * the modal, every open shows current data.
 */
const CarDetailBody = props => {
  const { id, mode } = props
  const asModal = mode === 'modal'

  const [car, setCar] = useState(null)
  const [loading, setLoading] = useState(false)

  const getCar = async () => {
    if (!id) return
    const response = await getRequest(`${carsUrl}/${id}`, 'car', setLoading)
    if (response.success) {
      setCar(response.data)
    } else if (response.error.response) {
      errorNotification(response.error.response.data.message)
    }
  }

  useEffect(() => {
    getCar()
    // eslint-disable-next-line
  }, [id])

  const title = car
    ? `${car.year || ''} ${car.make?.name || ''} ${car.model?.name ||
        ''} ${car.trim || ''}`.trim()
    : ''

  return (
    <PageLoader className={asModal ? '' : 'container-fluid'} loading={loading}>
      {/* Only the routed page owns the browser tab's title — a modal opened over
          the list must not rename the tab the list is sitting in. */}
      {/* The car itself, and the brand once — this used to print the car's name alone, which
          read as a different site in the tab strip. An unloaded car falls back to "Cars", which
          is what the route floor would have said anyway. */}
      {!asModal && <PageTitle title={title || 'Cars'} />}

      <div className="row">
        <div
          className={
            asModal ? 'col-md-12' : 'col-lg-10 offset-lg-1 col-xl-6 offset-xl-0'
          }
        >
          <div className={asModal ? '' : 'card mb-4rem'}>
            {!asModal && <div className="card-header">Car Details</div>}
            <div className={asModal ? '' : 'card-body'}>
              {asModal && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}
                >
                  <b>{title}</b>
                  <Space size={4}>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={getCar}
                    >
                      Refresh
                    </Button>
                  </Space>
                </div>
              )}
              {car ? (
                <DetailSection
                  car={car}
                  payload={car}
                  handleSuccess={() => getCar()}
                />
              ) : null}
            </div>
          </div>
        </div>

        {/* Images live on the routed page only: TableSection reads the car id
            straight off the route, which a modal has no equivalent of. */}
        {!asModal && car ? (
          <div className="col-lg-10 offset-lg-1 col-xl-6 offset-xl-0">
            {/* What the Telegram bot (and the background checks) changed on this car — nothing is
                drawn for a car they have never touched. */}
            <RecentChangesCard carId={id} />
            <TableSection car={car} handleCalback={() => {}} />
          </div>
        ) : null}
      </div>
    </PageLoader>
  )
}

CarDetailBody.propTypes = {
  /** Which car to show. */
  id: PropTypes.string,
  /** 'page' (default, routed) or 'modal' (the list's quick view). */
  mode: PropTypes.oneOf(['page', 'modal'])
}

CarDetailBody.defaultProps = { mode: 'page' }

export default CarDetailBody
